/**
 * Queue / attention row → deep link with app, focus pane, and item highlight params.
 */

import { navItemAllowed, routePath } from "./master";
import { withAppFocus } from "./subflow";

const GATE_TARGET = {
  G1: { path: "/assessment", focus: "g1" },
  G2: { path: "/decision", focus: "authorize", gate: "G2" },
  G3: { path: "/governance", focus: "gates-early", gate: "G3" },
  G4: { path: "/governance", focus: "gates-early", gate: "G4" },
  G5: { path: "/delivery", focus: "gates", gate: "G5" },
  G6: { path: "/delivery", focus: "gates", gate: "G6" },
  G7: { path: "/delivery", focus: "gates", gate: "G7" },
  G8: { path: "/delivery", focus: "gates", gate: "G8" },
};

export function resolveActionAppId(item, fallback) {
  const direct = item?.application || item?.app;
  if (direct) return direct;
  const title = item?.title || "";
  const match = title.match(/APP-\d+/);
  return match?.[0] || fallback || "";
}

/** Map a queue row to route path, sub-flow focus, gate highlight, and fact-state filter. */
export function actionTargetFromItem(item) {
  if (!item) return { path: "/", focus: "overview" };

  const gate = item.gate || null;
  const kind = item.kind;
  const severity = item.severity;
  const baseRoute = item.route || "/";
  const pathOnly = routePath(baseRoute);
  const routeParams = new URLSearchParams(String(baseRoute).split("?")[1] || "");

  if (kind === "evidence" || (severity === "WARNING" && !gate)) {
    const states = String(item.state_filter || routeParams.get("state") || "").split(",").filter(Boolean);
    if (!states.length) {
      if (item.conflicting) states.push("CONFLICTING");
      if (item.unknown) states.push("UNKNOWN");
    }
    if (!states.length) states.push("UNKNOWN");
    const primary = states.includes("CONFLICTING") ? "CONFLICTING" : states[0];
    return { path: "/evidence", focus: "facts", gate: null, state: states.join(",") || primary };
  }

  if (gate && GATE_TARGET[gate]) {
    return { ...GATE_TARGET[gate] };
  }

  if (kind === "decision" || severity === "DECISION REQUIRED") {
    return { path: "/decision", focus: "authorize", gate: "G2" };
  }

  if (kind === "approval" || severity === "APPROVAL REQUIRED") {
    if (gate && GATE_TARGET[gate]) return { ...GATE_TARGET[gate] };
    return { path: pathOnly, focus: routeParams.get("focus") || "gates-early", gate };
  }

  return {
    path: pathOnly,
    focus: routeParams.get("focus") || "overview",
    gate: routeParams.get("gate") || gate || null,
    state: routeParams.get("state") || null,
  };
}

function allowedPathForGuide(path, guide) {
  if (!guide?.role) return path;
  if (navItemAllowed(path, guide)) return path;
  const home = guide.home || "/";
  if (navItemAllowed(home, guide)) return home;
  const nav = guide.nav || [];
  return nav.find((href) => navItemAllowed(href, guide)) || "/";
}

export function actionOpenHref(item, guide, fallbackAppId) {
  const appId = resolveActionAppId(item, fallbackAppId);
  const target = actionTargetFromItem(item);
  const path = allowedPathForGuide(target.path, guide);
  const focus = path === target.path ? target.focus : "overview";

  const [pathname, query = ""] = withAppFocus(path, appId, focus).split("?");
  const params = new URLSearchParams(query);
  if (target.gate && path === target.path) params.set("gate", target.gate);
  if (target.state && path === target.path) params.set("state", target.state);
  const encoded = params.toString();
  return encoded ? `${pathname}?${encoded}` : pathname;
}

export function canOpenAction(item, guide) {
  const target = actionTargetFromItem(item);
  return navItemAllowed(target.path, guide);
}

export function parseStateFilter(search) {
  const raw = new URLSearchParams(search).get("state");
  if (!raw) return null;
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

export function factsMatchingStates(facts, states) {
  if (!states?.length) return facts;
  const set = new Set(states);
  return facts.filter((f) => set.has(f.state));
}
